import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import dayjs from "dayjs";
import dayjsEs from "dayjs/locale/es";
import dayjsEn from "dayjs/locale/en";

import { DashboardAction, EngineDashboardElement, TraslatedItem } from "../types/types";

import { useLocaleProvider } from "../../../components/locale-provider";
import axios from "http/customAxios";
import { notificationsMessages } from "../messages";
import { getLanguageFromFullLocale, IConfig, useConfig } from "../../../lib/config";
import { useKeycloakAuthz } from "../../../components/keycloak-provider";
import { LOCALE } from "../../witadmin/Common/constants";
import {useTheme} from "styled-components";

type ListNotificationsProp = {
  data: EngineDashboardElement[];
};

function getTranslatedData(translationList, currentLocale) {
  return (
    translationList.find(item => item.locale === currentLocale) ||
    translationList.find(item => item.locale === LOCALE.es)
  ); // fallback to locale ES
}

export const Notifications = ({ data }: ListNotificationsProp) => {
  const [notifications, setNotifications] = useState(data);
  const intl = useIntl();
  const localeProvider = useLocaleProvider();
  const keycloak = useKeycloakAuthz();
  const theme = useTheme();

  const configuration: IConfig = useConfig();

  const currentLocale = useMemo(
    () => localeProvider && getLanguageFromFullLocale(localeProvider.currentLocale),
    [localeProvider]
  );

  const showTranslatedDate = useCallback(
    (createdAt?: Date) => {
      dayjs.locale(currentLocale === "en" ? dayjsEn : dayjsEs);
      return dayjs(createdAt).format("dddd DD MMMM");
    },
    [currentLocale]
  );

  const handleClick = useCallback(
    (item: EngineDashboardElement, index: number, translatedItem: TraslatedItem) => {
      const { action, read = "", id } = item;
      const { value } = translatedItem;

      const readNotification = async () => {
        const response = await axios.put(
          `${configuration.notificationsWitbookingUrl}/${id}/read`,
          {},
          {
            headers: {
              Authorization: keycloak.rpt,
            },
          }
        );
        if (response.status === 200) {
          const newNotifications = [...notifications];
          newNotifications[index].read = true;
          setNotifications(newNotifications);
        }
      };

      const defineAction: any = {
        [DashboardAction.OPEN_URL]: () => {
          window.open(value.toString(), "_blank");
        },
      };
      const doAction = defineAction[action]
        ? defineAction[action]
        : console.info(`Not supported ${action}`);

      doAction();

      if (!read) {
        return readNotification();
      }
    },
    [configuration.notificationsWitbookingUrl, notifications, keycloak]
  );

  return (
    <section className="border rounded border-[#EAEBED] h-full overflow-y-auto overflow-x-hidden">
      <header className="text-base font-semibold m-4">
        {intl.formatMessage(notificationsMessages.notificationsTitle)}
      </header>
      {notifications &&
        notifications.map((item: EngineDashboardElement, index: number) => {
          const { read = false, id, createdAt, translationList } = item;
          const translatedItem = getTranslatedData(translationList, currentLocale);
          return (
            <article
              key={id}
              className="flex flex-row px-4 py-[12px] bg-[#F1F5FF] mx-4 my-2.5 items-center rounded">
              <div
                className={`p-1.5 rounded-full bg-${
                  !read ? `[${theme.buttonColor}]` : "transparent border-[#979CB1] border-[1px] border-solid"
                } `}
              />
              <div className="flex flex-col ml-[15px]">
                <span
                  className={`text-sm leading-[15px] font-semibold text-[${
                    !read ? theme.textColor : "#979CB1"
                  }]`}>
                  {translatedItem.label}
                </span>
                <span
                  onClick={() => handleClick(item, index, translatedItem)}
                  className={`text-xs font-semibold leading-[18px] text-[${!read ? `${theme.buttonColor}` : `#979CB1`}]
                  text-left hover:underline cursor-pointer mt-[10px]`}>
                  {translatedItem.actionLabel}
                </span>
                <span className={`mt-2 text-[#979CB1] capitalize text-[10px]`}>
                  {showTranslatedDate(createdAt)}
                </span>
              </div>
            </article>
          );
        })}
    </section>
  );
};
